import { ReactNode, useEffect, useRef, useState } from 'react';

import { useQueryState } from 'nuqs';

import { useListIntersectionObserver } from '@hooks/useListIntersectionObserver';
import { useScrollToElement } from '@hooks/useScrollToElement';
import { QueryStringKey } from '@lib/url/queryUtils';
import { MAIN_NAVIGATION_MIN_HEIGHT } from '../../../../Navigation/MainNavigation/constants/constants';
import { PostViewOptions } from '../../../constants/constants';

import { PostsViewerSection } from '../../PostsViewerSection/PostsViewerSection';
import { Post } from '../../../types/Post';
import { PostList } from './PostsList/PostList';
import InfiniteScroll from '../../../../InfiniteScroll/InfiniteScroll';

interface CatchesPostsViewProps {
  posts: Post[];
  currentPostId: string | undefined;
  hasMore: boolean;
  loadMore: () => void;
  onBackButtonClick: (catchPostId?: string) => void;
  onViewChange: (viewName: PostViewOptions, viewCatchId?: string) => void;
  showMoreTeaser?: ReactNode;
  hasCatch?: boolean;
}

export const PostsListView = ({
  posts,
  currentPostId,
  hasMore,
  loadMore,
  onBackButtonClick,
  onViewChange: handleViewChange,
  showMoreTeaser = null,
}: CatchesPostsViewProps) => {
  const postListReference = useRef<HTMLDivElement>(null);
  const [postListHTMLItems, setPostListHTMLItems] = useState<HTMLElement[]>([]);
  const observerEntry = useListIntersectionObserver(postListHTMLItems, {
    threshold: 0.5,
  });
  const visiblePostId = observerEntry?.target.id ?? '';
  const [, setCurrentPostId] = useQueryState(QueryStringKey.CurrentPostViewerId);

  useScrollToElement(postListReference, `[id="${currentPostId}"]`, {
    offsetFromTop: MAIN_NAVIGATION_MIN_HEIGHT,
    scrollOnlyOnce: true,
    scrollBehavior: 'instant' as ScrollBehavior, // as ScrollBehavior is a workaround for https://github.com/microsoft/TypeScript/issues/47441
  });

  useEffect(() => {
    const updatePostIdInUrl = async (): Promise<void> => {
      if (visiblePostId) {
        await setCurrentPostId(visiblePostId, { scroll: false, shallow: true });
      }
    };

    void updatePostIdInUrl();
  }, [setCurrentPostId, visiblePostId]);

  useEffect(() => {
    if (posts.length === 0) {
      return;
    }
    setPostListHTMLItems(Array.from(postListReference.current?.querySelectorAll('li') ?? []));
  }, [posts.length]);

  const handleBackButtonClick = (): void => {
    onBackButtonClick(visiblePostId || currentPostId);
  };

  return (
    <PostsViewerSection
      className="overflow-y-auto"
      id="catches-posts-view"
      navigationLocationName="Catches"
      onBackButtonClick={handleBackButtonClick}
      ref={postListReference}
    >
      <InfiniteScroll
        hasMore={hasMore}
        next={loadMore}
        dataLength={posts.length}
        scrollableTarget="catches-posts-view"
        loader={<></>}
      >
        <PostList posts={posts} onViewChange={handleViewChange} />
      </InfiniteScroll>
      {showMoreTeaser && <div className="bg-sea-foam -mt-8 w-full py-10">{showMoreTeaser}</div>}
    </PostsViewerSection>
  );
};
